// Geo-aware price display — Stripe always charges USD, this is display-only.
//
// Saudi Arabia (SA) visitors: see SAR only ("50 ريال"). No USD shown.
// UAE (AE) visitors:           see AED only ("55 درهم إماراتي"). No USD shown.
// All other countries:         see USD only ("$27"). No local-currency.
//
// The numeric values are marketing-chosen psychological prices, not strict FX conversions.

// Country-specific local price maps.
//   key  = USD amount Stripe actually charges
//   value = the number shown to the visitor in their local currency
const LOCAL_PRICE_MAP = {
  SA: { 27: 99, 22: 79 },   // Saudi Arabia — SAR
  AE: { 27: 55, 22: 45 },   // UAE — AED
};

function localValue(usdAmount, country) {
  if (typeof usdAmount !== "number" || !Number.isFinite(usdAmount)) return 0;
  const key = Math.round(usdAmount);
  const map = country === "SA" ? LOCAL_PRICE_MAP.SA : LOCAL_PRICE_MAP.AE;
  if (key in map) return map[key];
  // Fallback for any unmapped USD value — approximate FX (1 USD ≈ 3.75 SAR/AED).
  return Math.round(usdAmount * 3.75);
}

function fmtUsd(amount) {
  // Strip trailing ".00" for whole dollars, keep up to 2 decimals otherwise.
  const fixed = amount.toFixed(2);
  return fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
}

export function priceDisplay(usdAmount, country) {
  if (country === "SA") {
    const v = localValue(usdAmount, "SA");
    return {
      primary: `${v} ريال`,
      primaryNumber: v,
      currencyShort: "ريال",
      secondary: null,
      isSA: true,
      isAE: false,
    };
  }
  if (country === "AE") {
    const v = localValue(usdAmount, "AE");
    return {
      primary: `${v} درهم إماراتي`,
      primaryNumber: v,
      currencyShort: "درهم",
      secondary: null,
      isSA: false,
      isAE: true,
    };
  }
  // Every other country — show USD only, no local conversion.
  return {
    primary: `$${fmtUsd(usdAmount)}`,
    primaryNumber: Math.round(usdAmount),
    currencyShort: "USD",
    secondary: null,
    isSA: false,
    isAE: false,
  };
}

// Helper for "compare value" anchor lines that use a different reference price
// (e.g. expert session = 750 local units). Keeps wording consistent.
export function localCurrency(country) {
  if (country === "SA") return "ريال";
  if (country === "AE") return "درهم";
  return "USD";
}

// Backwards-compat: some legacy components still import sarDisplay.
export function sarDisplay(usdAmount) {
  return localValue(usdAmount, "SA");
}

// Pink Book — geo-aware display label.
//   SA → "99 ريال" (special Saudi discount: $26.24 USD wire),
//   AE → "149 درهم" ($40 USD), KW → "12 دينار" ($40 USD), ROW → "$40" USD.
// Stripe wire amount is set on the backend at order-create time to match the
// label exactly so the customer's bank statement aligns with what they saw.
export function pinkPriceLabel(country) {
  const c = (country || "").toUpperCase();
  if (c === "SA") return { primary: "99 ريال", number: 99, currencyShort: "ريال" };
  if (c === "AE") return { primary: "149 درهم", number: 149, currencyShort: "درهم" };
  if (c === "KW") return { primary: "12 دينار", number: 12, currencyShort: "دينار" };
  return { primary: "$40", number: 40, currencyShort: "USD" };
}
